import { useEffect, useRef, useState } from "react";
import { STORY_SOUNDS, startAmbient } from "@/lib/ambient";

const TIMERS = [[15, "15 min"], [30, "30 min"], [45, "45 min"], ["end", "When story ends"]];
const FADE = 30;

const fadeAudio = (a, sec) => {
  if (!a || a.paused) return;
  const start = a.volume, t0 = Date.now();
  const id = setInterval(() => {
    const k = Math.min((Date.now() - t0) / (sec * 1000), 1);
    a.volume = Math.max(start * (1 - k), 0);
    if (k >= 1) { clearInterval(id); a.pause(); a.volume = start; }
  }, 200);
};

// Ambient sound under the story + a sleep timer that fades everything out
export const useSleepMix = (audioRef) => {
  const amb = useRef(null);
  const timer = useRef(null);
  const [sound, setSoundState] = useState("off");
  const [mode, setMode] = useState("end");
  const [endsAt, setEndsAt] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => () => { amb.current?.stop(); clearTimeout(timer.current); }, []);
  useEffect(() => { if (!endsAt) return undefined; const id = setInterval(() => tick((x) => x + 1), 15000); return () => clearInterval(id); }, [endsAt]);
  const fadeAll = () => { amb.current?.fadeOut(FADE); amb.current = null; setSoundState("off"); fadeAudio(audioRef.current, FADE); setEndsAt(null); };
  const setSound = (id) => {
    amb.current?.stop(); amb.current = null;
    if (id !== "off") amb.current = startAmbient(id, 0.22);
    setSoundState(id);
  };
  const setTimer = (m) => {
    clearTimeout(timer.current);
    setMode(m);
    if (m === "end") { setEndsAt(null); return; }
    timer.current = setTimeout(fadeAll, m * 60000 - FADE * 1000);
    setEndsAt(Date.now() + m * 60000);
  };
  const storyEnded = () => { if (mode === "end") { amb.current?.fadeOut(FADE); amb.current = null; setSoundState("off"); } };
  return { sound, setSound, mode, setTimer, endsAt, storyEnded };
};

export const SleepMix = ({ mix }) => (
  <div className="rounded-2xl bg-white/5 p-4 mt-4 space-y-3" data-testid="sleep-mix">
    <div>
      <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Sound underneath</div>
      <div className="flex flex-wrap gap-2">
        {STORY_SOUNDS.map((s) => (
          <button key={s.id} onClick={() => mix.setSound(s.id)} className={`rounded-full px-3 py-1.5 text-sm border transition-colors ${mix.sound === s.id ? "bg-[#F3C98B] text-[#141A33] border-[#F3C98B]" : "border-white/20 hover:bg-white/10"}`} data-testid={`sleep-sound-${s.id}`}>{s.icon} {s.name}</button>
        ))}
      </div>
    </div>
    <div>
      <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Sleep timer · fades out gently</div>
      <div className="flex flex-wrap gap-2">
        {TIMERS.map(([v, label]) => (
          <button key={v} onClick={() => mix.setTimer(v)} className={`rounded-full px-3 py-1.5 text-sm border transition-colors ${mix.mode === v ? "bg-[#F3C98B] text-[#141A33] border-[#F3C98B]" : "border-white/20 hover:bg-white/10"}`} data-testid={`sleep-timer-${v}`}>{label}</button>
        ))}
      </div>
      {mix.endsAt && <p className="text-xs opacity-60 mt-2" data-testid="sleep-timer-left">Fading out in about {Math.max(1, Math.round((mix.endsAt - Date.now()) / 60000))} min</p>}
    </div>
  </div>
);
